import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Alert, Box, Button, Divider, FormControl, Grid, LinearProgress, Typography } from "@mui/material";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { USER_ROLE_ADMIN } from "core/useSession";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 } from "uuid";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { BrokerIcon } from "../Icons/Icons";
import AgentAutoComplete from "./AgentAutoComplete";
import BuyersGrid from "./BuyersGrid";
import { ClientAccountsList } from "./ClientAccountsList";
import CropProfileGrid from "./CropProfileGrid";

// ---

export default function ClientEditWorkspace() {
  const { globalState } = useContext(AppContext);
  const { member_id } = useParams();
  const { addAttachments } = useChatAssistantAttachments();
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();
  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [isBuyerModalOpen, setIsBuyerModalOpen] = useState(false);
  const [isCropProfileModalOpen, setIsCropProfileModalOpen] = useState(false);

  // ---

  const [member, dispatchMember] = useReducer(propUpdateReducer, {
    country_code: "US",
    is_active: true,
  });

  // ---

  const [brokers, setBrokers] = useState(null);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false);
  const selectedBroker = useMemo(() => {
    const _selectedBroker =
      brokers?.find((broker) => member?.agent_token?.toLowerCase() == broker?.agent_token?.toLowerCase()) || null;

    return _selectedBroker;
  }, [brokers, member?.agent_token, globalState?.agent_token]);

  const handleChangeAgentToken = async (agent) => {
    handleChangeState({ target: { name: "agent_token" } }, agent?.agent_token);
  };

  // The Users API rejects another agent's token on a new client, so agents can only create clients for themselves.
  const isAgentCreatingClient = String(member_id) === "0" && globalState?.user_role !== USER_ROLE_ADMIN;

  useEffect(() => {
    if (isAgentCreatingClient && globalState?.agent_token && member?.agent_token !== globalState.agent_token) {
      dispatchMember({ type: "update", payload: { key: "agent_token", value: globalState.agent_token } });
    }
  }, [isAgentCreatingClient, globalState?.agent_token, member?.agent_token]);

  const loadBrokers = async () => {
    try {
      if (globalState?.agent_token) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {
          agent_token: globalState?.agent_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      } else if (globalState?.user_role == USER_ROLE_ADMIN) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {};
        // Brokers create clients and assign them to any chosen agent_token.
        // So do not specify agent_token OR producer_token here.
        const _apiConfig = {};
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      }
    } catch (err) {
      setIsLoadingBrokers(false);
      setBrokers(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.agent_token || globalState?.user_role == USER_ROLE_ADMIN) {
      setIsLoadingBrokers(true);
      loadBrokers();
    }
  }, [globalState?.agent_token, globalState?.user_role]);

  const loadPrimaryBroker = useCallback(async () => {
    try {
      const _userGrants = await UsersApiHelper.selectUserGrants(
        {
          producer_token: member?.producer_token,
          is_active: true,
        },
        globalState
      );

      if (_userGrants?.length) {
        const _primaryAgentToken = _userGrants[0]?.agent_token;

        dispatchMember({
          type: "update",
          payload: { key: "agent_token", value: _primaryAgentToken },
        });
      }
    } catch (err) {
      handleError(err);
    }
  }, [member?.producer_token, globalState, dispatchMember]);

  useEffect(() => {
    const canLoadPrimary =
      member?.id && !member.agent_token && (globalState?.agent_token || globalState?.user_role === USER_ROLE_ADMIN);

    if (canLoadPrimary) {
      setIsLoadingBrokers(true);
      loadPrimaryBroker();
    }
  }, [member?.id, member?.agent_token, globalState?.agent_token, globalState?.user_role, loadPrimaryBroker]);

  // --- --- ---

  const handleAddBuyer = () => {
    setIsBuyerModalOpen(true); // Open the buyer modal
  };
  const handleCloseBuyerModal = () => {
    setIsBuyerModalOpen(false); // Reset to false when buyer modal is closed
  };

  const handleAddCropProfile = () => {
    setIsCropProfileModalOpen(true); // Open the crop profile modal
  };

  const handleCloseCropProfileModal = () => {
    setIsCropProfileModalOpen(false); // Reset to false when crop profile modal is closed
  };

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  // ---

  const loadMember = async (_memberID) => {
    try {
      const _memberFilter = {
        id: _memberID || member_id,
      };
      const members = await UsersApiHelper.users_selectObject("Member", _memberFilter, {});
      if (members?.length) {
        dispatchMember({
          type: "updateMany",
          payload: members[0],
        });
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      dispatchMember({
        type: "replace",
        payload: {},
      });

      handleError(err);
    }
  };

  useEffect(() => {
    if (member && member.id) {
      addAttachments([{ name: "Client Details", content: member }]);
    }
  }, [member]);

  useEffect(() => {
    if (member_id && String(member_id) !== "0" && !member?.id) {
      loadMember();
    } else if (String(member_id) === "0" && !member?.id) {
      const _newMemberID = v4();
      dispatchMember({
        type: "update",
        payload: { key: "id", value: _newMemberID },
      });

      setIsLoading(false);
    }
  }, [globalState, member?.id, member_id]);

  // ---

  const handleChangeState = async (event, value) => {
    value = value ?? event.target.value;

    if (member[event.target.name] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatchMember({
        type: "update",
        payload: { key: event.target.name, value: value },
      });

      const _newState = { ...member };
      _newState[event.target.name] = value;
      handleChangeClient(_newState);
    }
  };

  const handleChangeClient = async (account) => {
    try {
      if (account?.id) {
        if (!account.agent_token) {
          enqueueSnackbar("Choose an agent before the client can be saved.", {
            variant: "warning",
            preventDuplicate: true,
          });
          return;
        }

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          _instance: null,
        });

        const _accountParams = { ...account };
        const isNewProducer = !_accountParams.producer_token;
        let createdProducerToken = null;
        if (isNewProducer) {
          // marketing/Member enforces UserGrant before Member_Update runs; the proc creates the first UserGrant.
          // Do not send producer_token on first save or the API will require a grant that does not exist yet.
          createdProducerToken = v4();
          delete _accountParams.producer_token;
          _accountParams.new_producer_token = createdProducerToken;
        } else {
          delete _accountParams.new_producer_token;
        }

        const _apiConfig = { agent_token: _accountParams.agent_token };
        setIsSavingClient(true);
        await api.callPost("marketing/Member", _accountParams, _apiConfig);
        if (isNewProducer && createdProducerToken) {
          dispatchMember({
            type: "updateMany",
            payload: { producer_token: createdProducerToken },
          });
        }
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsSavingClient(false);
    }
  };

  // ---

  const formMaxWidth = "1000px";

  return (
    <>
      <WorkspaceHeader
        title={String(member_id) !== "0" ? "Client" : "New Client"}
        category={{
          title: "Broker",
          icon: <BrokerIcon />,
        }}
        breadCrumbs={[
          {
            title: "Clients",
            path: "/broker/clients",
          },
        ]}
      />

      <Box sx={{ maxWidth: formMaxWidth }}>
        {isSavingClient && <LinearProgress sx={{ mb: 2 }} color="primary" aria-label="Saving client" />}

        {String(member_id) === "0" && !member?.agent_token && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select the client&apos;s agent first. Saves run only after an agent is chosen so the server can authorize
            the request.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <AgentAutoComplete
              brokers={brokers}
              selectedBroker={selectedBroker}
              onChange={handleChangeAgentToken}
              multiple={false}
              disabled={isAgentCreatingClient}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Business Name"
                name="business_name"
                isRequired={true}
                defaultValue={member?.business_name || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="First Name"
                name="first_name"
                isRequired={true}
                defaultValue={member?.first_name || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Last Name"
                name="last_name"
                isRequired={true}
                defaultValue={member?.last_name || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Email"
                name="email"
                isRequired={true}
                defaultValue={member?.email || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="City"
                name="city"
                isRequired={true}
                defaultValue={member?.city || ""}
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} className="clientState">
            <DataFormSelect
              value={member?.state_abbreviation}
              name="state_abbreviation"
              onChange={handleChangeState}
              objectName="State"
              itemFilter={{ reinsurance_year: getCurrentReinsuranceYear() }}
              itemGetKey={(item) => item.state_abbreviation}
              itemGetLabel={(item) => item.state_abbreviation}
              doFetch={true}
              isLoading={isLoading}
              isSmall={true}
              multiple={false}
            />
          </Grid>
        </Grid>

        <Divider />

        <ClientAccountsList member={member} onChange={handleChangeState} />
      </Box>

      <Box>
        {Boolean(member?.producer_token) && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mr: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Crop Profiles
              </Typography>
              <Button variant="outlined" onClick={handleAddCropProfile}>
                <AddCircleOutline /> Add Crop Profile
              </Button>
            </Box>
            <CropProfileGrid
              producer_token={member?.producer_token}
              onAddCropProfile={isCropProfileModalOpen}
              onClose={handleCloseCropProfileModal}
            />

            <Divider />

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mr: 3 }}>
              <Typography variant="h6">Buyers</Typography>
              <Button variant="outlined" onClick={handleAddBuyer}>
                <AddCircleOutline /> Add Buyer
              </Button>
            </Box>
            <BuyersGrid
              producer_token={member?.producer_token}
              onAddBuyer={isBuyerModalOpen} // Pass state to control modal visibility
              onClose={handleCloseBuyerModal}
            />
          </>
        )}
      </Box>
      {confirmDialogComponent}
    </>
  );
}
